// Localized wording for a key/capo setting, shared by the key panel, toasts, print and text export.

import { soundingKey, type KeyQuality, type KeySetting } from "../core/theory/transpose";

type T = (key: string, params?: Record<string, string | number>) => string;
type SongKey = { tonic: string; quality: KeyQuality };

/** "Bb" → "B♭", "C#m" → "C♯m" for on-screen text (exports keep ASCII) */
export function noteGlyphs(name: string): string {
  return name.replace(/^([A-G])#/, "$1♯").replace(/^([A-G])b/, "$1♭");
}

export function signed(n: number, ascii = false): string {
  return n > 0 ? `+${n}` : `${ascii ? "-" : "−"}${Math.abs(n)}`;
}

/** "+2 (B 키) · 카포 4", "원곡 키 · 카포 4", "원곡 키" */
export function settingLabel(t: T, key: SongKey, s: KeySetting): string {
  const k = s.transpose ? `${signed(s.transpose)} (${t("key.key_of", { k: noteGlyphs(soundingKey(key, s.transpose)) })})` : t("key.original_key");
  return s.capo ? `${k} · ${t("key.capo_tag", { n: s.capo })}` : k;
}

/** header line of a printed/copied chart: "키 B (원곡 A, +2) · 83 BPM · 코드는 카포 4 기준 모양" */
export function chartMeta(t: T, key: SongKey, s: KeySetting, bpm: number | string, ascii = false): string {
  const fmt = (n: string) => (ascii ? n : noteGlyphs(n));
  let line = `${t("analysis.key")} ${fmt(soundingKey(key, s.transpose))}`;
  if (s.transpose) line += ` (${t("key.orig_of", { k: fmt(soundingKey(key, 0)) })}, ${signed(s.transpose, ascii)})`;
  line += ` · ${bpm} BPM`;
  if (s.capo) line += ` · ${t("key.capo_shapes", { n: s.capo })}`;
  return line;
}
